import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthProvider';
import AdminPanel from '../components/admin/AdminPanel3';
import { getFirestore, getDoc, doc } from 'firebase/firestore';
import app from '../Firebase';
import LoadingPage from '../components/LoadingPage';

// Protected Route component for admin-only access
const ProtectedAdminRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { currentUser, loading } = useAuth();
  
  // Check if the user has the APP_ADMIN permission
  const [isAdmin, setIsAdmin] = React.useState<boolean | null>(null);
  const [checkingPermission, setCheckingPermission] = React.useState<boolean>(true);
  
  // Fetch user permissions to check for APP_ADMIN role
  React.useEffect(() => {
    const checkAdminPermission = async () => {
      if (!currentUser || !currentUser.email) {
        setIsAdmin(false);
        setCheckingPermission(false);
        return;
      }
      
      try {
        const db = getFirestore(app);
        const userDocRef = doc(db, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userPermissions = userData?.permissions || [];
          setIsAdmin(userPermissions.includes('APP_ADMIN'));
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin permission:', error);
        setIsAdmin(false);
      } finally {
        setCheckingPermission(false);
      }
    };
    
    if (currentUser) {
      checkAdminPermission();
    } else if (!loading) {
      setIsAdmin(false);
      setCheckingPermission(false);
    }
  }, [currentUser, loading]);
  
  if (loading || checkingPermission) {
    return <LoadingPage />;
  }
  
  if (!currentUser) {
    return <Navigate to="/login?redirect=/admin3" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return element;
};

const AdminRoute: React.FC = () => {
  const db = getFirestore(app);
  
  return (
    <Routes>
      <Route path="/" element={
        <ProtectedAdminRoute 
          element={<AdminPanel db={db} />} 
        />
      } />
    </Routes>
  );
};

export default AdminRoute;